/**
 * GovFinancialsPage — Pipeline value, backlog, and pay app aging.
 * Owner-facing "are we making money" dashboard. Aggregates from
 * gov_solicitations + gov_construction_jobs + gov_pay_applications.
 * Route: /gov/financials
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { S, GOV_COLORS, fmtMoney, fmtMoneyFull } from './govShared';

interface Financials {
  summary: {
    pipeline_value: number;
    submitted_value: number;
    awarded_ytd_value: number;
    lost_value: number;
    backlog_value: number;
    total_invoiced: number;
    total_paid: number;
    total_retention: number;
    outstanding_ar: number;
    active_jobs_count: number;
    open_pay_apps_count: number;
  };
  submitted_bids: any[];
  recent_awards: any[];
  recent_losses: any[];
  active_jobs: any[];
  open_pay_apps: any[];
}

const AGING_COLORS: Record<string, string> = {
  '0_30': '#22C55E',
  '31_60': '#EAB308',
  '61_90': '#F97316',
  '90_plus': '#EF4444',
  not_submitted: '#64748B',
};
const AGING_LABELS: Record<string, string> = {
  '0_30': '0-30d',
  '31_60': '31-60d',
  '61_90': '61-90d',
  '90_plus': '90+d',
  not_submitted: 'Not Submitted',
};

export default function GovFinancialsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<Financials | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/gov/financials');
      setData(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div style={{ ...S.container, ...S.loading }}>Loading financials…</div>;
  if (!data) return <div style={{ ...S.container, ...S.empty }}>Failed to load financials.</div>;

  const s = data.summary;
  const winRate = data.recent_awards.length + data.recent_losses.length > 0
    ? (data.recent_awards.length / (data.recent_awards.length + data.recent_losses.length)) * 100
    : null;

  return (
    <div style={S.container}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>💰 Financials</h1>
          <p style={S.subtitle}>Pipeline value · backlog · pay application aging · YTD performance</p>
        </div>
      </div>

      {/* Top-line KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <KPI label="Pipeline Value" value={fmtMoney(s.pipeline_value)} sub="Estimated value of live opportunities" color={GOV_COLORS.accent} />
        <KPI label="Submitted (Awaiting Decision)" value={fmtMoney(s.submitted_value)} sub={`${data.submitted_bids.length} bid${data.submitted_bids.length === 1 ? '' : 's'}`} color={GOV_COLORS.caution} />
        <KPI label="Awarded YTD" value={fmtMoney(s.awarded_ytd_value)} sub={`${data.recent_awards.length} award${data.recent_awards.length === 1 ? '' : 's'}`} color={GOV_COLORS.success} />
        <KPI label="Win Rate" value={winRate == null ? '—' : `${winRate.toFixed(0)}%`} sub={`${data.recent_awards.length}W · ${data.recent_losses.length}L`} color={GOV_COLORS.info} />
      </div>

      {/* Construction backlog + AR */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        <KPI label="Backlog" value={fmtMoney(s.backlog_value)} sub={`${s.active_jobs_count} active job${s.active_jobs_count === 1 ? '' : 's'}`} color={GOV_COLORS.accent} />
        <KPI label="Invoiced To Date" value={fmtMoney(s.total_invoiced)} sub="Across all construction" color={GOV_COLORS.info} />
        <KPI label="Outstanding A/R" value={fmtMoney(s.outstanding_ar)} sub={`${s.open_pay_apps_count} open pay app${s.open_pay_apps_count === 1 ? '' : 's'}`} color={s.outstanding_ar > 0 ? GOV_COLORS.warning : GOV_COLORS.success} />
        <KPI label="Retention Held" value={fmtMoney(s.total_retention)} sub="Awaiting final acceptance" color={GOV_COLORS.caution} />
      </div>

      {/* Two-column layout: submitted bids + active jobs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div style={S.card}>
          <h3 style={S.panelTitle}>📤 Submitted Bids · Awaiting Decision</h3>
          {data.submitted_bids.length === 0 ? (
            <div style={{ ...S.empty, padding: 20 }}>No bids currently awaiting decision.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {data.submitted_bids.map((b: any) => (
                  <tr key={b.id} onClick={() => navigate(`/gov/solicitations/${b.id}`)} style={{ cursor: 'pointer', borderBottom: `1px solid ${GOV_COLORS.border}` }}>
                    <td style={{ padding: '10px 0' }}>
                      <div style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{b.solicitation_number || '—'}</div>
                      <div style={{ color: GOV_COLORS.textMuted, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 300 }}>{b.title}</div>
                    </td>
                    <td style={{ padding: '10px 0', textAlign: 'right', color: '#fff', fontSize: 13, fontWeight: 700 }}>
                      {fmtMoney(b.our_bid_amount || b.estimated_value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={S.card}>
          <h3 style={S.panelTitle}>🏗️ Active Construction Jobs</h3>
          {data.active_jobs.length === 0 ? (
            <div style={{ ...S.empty, padding: 20 }}>No active construction jobs.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${GOV_COLORS.border}` }}>
                  <th style={{ textAlign: 'left', padding: '6px 0', color: GOV_COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>Job</th>
                  <th style={{ textAlign: 'right', padding: '6px 0', color: GOV_COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>Value</th>
                  <th style={{ textAlign: 'right', padding: '6px 0', color: GOV_COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>% Done</th>
                  <th style={{ textAlign: 'right', padding: '6px 0', color: GOV_COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>Remaining</th>
                </tr>
              </thead>
              <tbody>
                {data.active_jobs.map((j: any) => (
                  <tr key={j.id} onClick={() => navigate(`/gov/construction/${j.id}`)} style={{ cursor: 'pointer', borderBottom: `1px solid ${GOV_COLORS.border}` }}>
                    <td style={{ padding: '10px 0' }}>
                      <div style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{j.contract_number || j.bid_number || '—'}</div>
                      <div style={{ color: GOV_COLORS.textMuted, fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>{j.solicitation?.title}</div>
                    </td>
                    <td style={{ padding: '10px 0', textAlign: 'right', color: '#fff', fontSize: 12, fontWeight: 700 }}>{fmtMoney(j._contract_value)}</td>
                    <td style={{ padding: '10px 0', textAlign: 'right', color: GOV_COLORS.textMuted, fontSize: 12 }}>{j.percent_complete != null ? `${j.percent_complete}%` : '—'}</td>
                    <td style={{ padding: '10px 0', textAlign: 'right', color: GOV_COLORS.accent, fontSize: 12, fontWeight: 700 }}>{fmtMoney(j._remaining)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pay App Aging */}
      <div style={{ ...S.card, marginBottom: 24 }}>
        <h3 style={S.panelTitle}>📊 Pay Application Aging</h3>
        {data.open_pay_apps.length === 0 ? (
          <div style={{ ...S.empty, padding: 20 }}>No open pay applications.</div>
        ) : (
          <>
            {/* Aging summary bar */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 16 }}>
              {(['0_30', '31_60', '61_90', '90_plus', 'not_submitted'] as const).map(bucket => {
                const items = data.open_pay_apps.filter((p: any) => p.aging_bucket === bucket);
                const total = items.reduce((sum: number, p: any) => sum + Number(p.amount_requested || 0), 0);
                return (
                  <div key={bucket} style={{
                    padding: 12, background: '#0F1923', borderRadius: 8,
                    borderLeft: `3px solid ${AGING_COLORS[bucket]}`,
                  }}>
                    <div style={{ color: AGING_COLORS[bucket], fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>{AGING_LABELS[bucket]}</div>
                    <div style={{ color: '#fff', fontSize: 18, fontWeight: 800, marginTop: 4 }}>{fmtMoney(total)}</div>
                    <div style={{ color: GOV_COLORS.textMuted, fontSize: 11 }}>{items.length} pay app{items.length === 1 ? '' : 's'}</div>
                  </div>
                );
              })}
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${GOV_COLORS.border}` }}>
                  <th style={{ textAlign: 'left', padding: '6px 0', color: GOV_COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>Pay App</th>
                  <th style={{ textAlign: 'left', padding: '6px 0', color: GOV_COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>Period End</th>
                  <th style={{ textAlign: 'right', padding: '6px 0', color: GOV_COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>Requested</th>
                  <th style={{ textAlign: 'right', padding: '6px 0', color: GOV_COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>Approved</th>
                  <th style={{ textAlign: 'right', padding: '6px 0', color: GOV_COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>Age</th>
                </tr>
              </thead>
              <tbody>
                {data.open_pay_apps.map((p: any) => (
                  <tr key={p.id} onClick={() => navigate(`/gov/construction/${p.construction_job_id}`)} style={{ cursor: 'pointer', borderBottom: `1px solid ${GOV_COLORS.border}` }}>
                    <td style={{ padding: '10px 0', color: '#fff', fontSize: 13, fontWeight: 700 }}>#{p.pay_app_number || '—'}</td>
                    <td style={{ padding: '10px 0', color: GOV_COLORS.textMuted, fontSize: 12 }}>{p.period_end || '—'}</td>
                    <td style={{ padding: '10px 0', textAlign: 'right', color: '#fff', fontSize: 12, fontWeight: 700 }}>{fmtMoneyFull(p.amount_requested)}</td>
                    <td style={{ padding: '10px 0', textAlign: 'right', color: GOV_COLORS.textMuted, fontSize: 12 }}>{fmtMoneyFull(p.amount_approved)}</td>
                    <td style={{ padding: '10px 0', textAlign: 'right' }}>
                      <span style={{ ...S.chip, background: AGING_COLORS[p.aging_bucket] + '22', color: AGING_COLORS[p.aging_bucket] }}>
                        {p.days_open != null ? `${p.days_open}d` : '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* Recent Awards / Losses */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={S.card}>
          <h3 style={S.panelTitle}>🏆 Recent Awards (YTD)</h3>
          {data.recent_awards.length === 0 ? (
            <div style={{ ...S.empty, padding: 20 }}>No awards yet this year.</div>
          ) : (
            data.recent_awards.map((a: any) => (
              <div key={a.id} onClick={() => navigate(`/gov/solicitations/${a.id}`)} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 0', borderBottom: `1px solid ${GOV_COLORS.border}`, cursor: 'pointer',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{a.solicitation_number || '—'}</div>
                  <div style={{ color: GOV_COLORS.textMuted, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 350 }}>{a.title}</div>
                  {a.award_date && <div style={{ color: GOV_COLORS.textDim, fontSize: 11 }}>{a.award_date.substring(0, 10)}</div>}
                </div>
                <div style={{ color: GOV_COLORS.success, fontSize: 14, fontWeight: 800 }}>{fmtMoney(a.award_amount || a.our_bid_amount)}</div>
              </div>
            ))
          )}
        </div>

        <div style={S.card}>
          <h3 style={S.panelTitle}>📉 Recent Losses</h3>
          {data.recent_losses.length === 0 ? (
            <div style={{ ...S.empty, padding: 20 }}>No losses recorded.</div>
          ) : (
            data.recent_losses.map((l: any) => (
              <div key={l.id} onClick={() => navigate(`/gov/solicitations/${l.id}`)} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 0', borderBottom: `1px solid ${GOV_COLORS.border}`, cursor: 'pointer',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{l.solicitation_number || '—'}</div>
                  <div style={{ color: GOV_COLORS.textMuted, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 350 }}>{l.title}</div>
                </div>
                <div style={{ color: GOV_COLORS.danger, fontSize: 14, fontWeight: 800 }}>{fmtMoney(l.our_bid_amount || l.estimated_value)}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, sub, color }: { label: string; value: string; sub?: string; color: string }) {
  return (
    <div style={{ ...S.card, borderLeft: `4px solid ${color}` }}>
      <div style={{ color: GOV_COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ color: '#fff', fontSize: 28, fontWeight: 800, marginTop: 6 }}>{value}</div>
      {sub && <div style={{ color: GOV_COLORS.textMuted, fontSize: 12, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}
